/**
 * Object contributions — the seam that lets combatants feed the sight/cover closures. Without it
 * `terrainVisibility` reads only the terrain map, so a placed wall/boulder/deployable would have to
 * be re-modeled as terrain to affect LoS, at the cost of not being targetable. This module derives
 * the same **blocksCell / softCell / hardCell** shape `terrain.ts` already produces, but from
 * placed, **non-Destroyed** `object`/`deployable` combatants' footprints × their
 * {@link "./types" ObjectProfile} instead of `TerrainProperties` flags — so `terrainVisibility` can fold
 * both sources into the SAME closures it hands to the pure `visibility()` (one seam, not a
 * parallel geometry).
 *
 * **Destroyed objects contribute nothing**: a destroyed wall stops blocking sight/granting cover
 * the instant it's destroyed — cover/LoS is a live query (nothing caches it on `EncounterState`),
 * so the very next `terrainVisibility` call already reflects destruction once this seam excludes a
 * `Destroyed` object from the derivation. (The *movement*-side twin of this gate — occupancy no
 * longer blocking a destroyed object — lives directly in `movement.ts`'s `placements()`, not here;
 * see its own local kind-gate.)
 *
 * Shaped so a hypothetical future edge-HP extension could feed the same closures (an edge-mounted
 * destructible would derive an edge-keyed contribution instead of a cell-keyed one) — but no
 * façade is built for that today; only cell footprints are modeled.
 */
import type { Space } from "@lancer-vtt/space";
import type { Cell, Combatant, CombatantId } from "./types";
import { contributesToTerrain, hasStatus, isOpenDoor } from "./types";

/**
 * A placed, live (non-Destroyed) object/deployable combatant carrying a profile —
 * {@link contributesToTerrain} is the presence check; `objectProfile` is exclusive to
 * `object`/`deployable` kinds by construction (only `spawnObject`/`spawnObjectGroup` ever set it),
 * so a bare kind check would be redundant here. **An open door contributes nothing either** — the
 * twin of the Destroyed exclusion, gated through {@link isOpenDoor} so the containment (door-marker
 * required, not just the bare "Open" status) lives in one place.
 */
function liveProfiledObjects(combatants: Record<CombatantId, Combatant>): Combatant[] {
  return Object.values(combatants).filter(
    (c) => contributesToTerrain(c) && c.pos != null && !hasStatus(c.statuses, "Destroyed") && !isOpenDoor(c),
  );
}

/**
 * The cell-keyed sets `terrainVisibility` folds alongside its terrain-derived
 * closures — one `Set<string>` (space-keyed) per `ObjectProfile` flag. A cell may
 * appear in at most one of `softCoverCells`/`hardCoverCells` per object (a single
 * profile names one `cover` degree), though two overlapping objects could
 * theoretically contribute both — `terrainVisibility` ORs each set independently,
 * same as it already does for terrain, and hard cover naturally wins downstream
 * (`visibility()`'s own hard-outranks-soft fold).
 */
export type ObjectContributions = {
  blocksSightCells: Set<string>;
  softCoverCells: Set<string>;
  hardCoverCells: Set<string>;
};

/** An empty contribution set — the no-op default when no combatants are supplied. */
export function emptyObjectContributions(): ObjectContributions {
  return { blocksSightCells: new Set(), softCoverCells: new Set(), hardCoverCells: new Set() };
}

/**
 * Derive the sight/cover contribution of every placed, live object/deployable in
 * `combatants` — the encounter-layer binding `terrainVisibility` folds in. Reads
 * only `objectProfile.blocksSight`/`cover`; `blocksMovement` is consumed
 * separately by the movement seam (`movement.ts`), not here.
 *
 * `excludeCoverFrom` — when the
 * cover query's `to` footprint IS this combatant (an attack targeting the cover
 * object itself), it must not gain soft/hard cover from its own bulk: RAW's cover
 * check is about a target being "obscured or obstructed" by something else, and a
 * lone hard-cover object with nothing else on the map reads `cover: "soft"` off
 * its own footprint without this. Scoped to the ONE contributor whose id matches —
 * it skips only *this* combatant's own cells going into `softCoverCells`/
 * `hardCoverCells`, never the cells themselves, so another, overlapping object's
 * cover on the same cell still counts (the loop still visits every other live
 * object normally). `blocksSightCells` is untouched: an endpoint cell is never
 * consulted by `sight`'s spine walk (`los.ts`), so a target's own sight-blocking
 * never bites its own cover query regardless, and this stays scoped to cover only.
 *
 * **The exclusion is unconditional over ALL of this combatant's cells, not just the
 * `to` endpoint — which also fixes a second, non-occupancy case for a Size-2+
 * object.** `visibility()`'s spine walk (`los.ts`) inspects every INTERMEDIATE cell
 * on the line between an attacker and a `to` cell, endpoints excluded. For a
 * multi-cell footprint, a line aimed at ONE of the object's own cells can pass
 * through ANOTHER of its own cells first — an intermediate, not an endpoint — so
 * without this exclusion a big wall/boulder shot at its far cell still read hard
 * cover from its OWN near cell sitting on the line (pinned:
 * `packages/domain/src/encounter/terrain.test.ts`, "Size-2+ self-shielding via an
 * INTERMEDIATE footprint cell"). Because `excludeCoverFrom` drops the combatant's
 * cells from `softCoverCells`/`hardCoverCells` entirely — not just at the `to`
 * cell — that self-shield drops out too, for free, consistent with the occupancy
 * floor: an object shouldn't shield itself on either path.
 */
export function objectContributions(
  space: Space<Cell>,
  combatants: Record<CombatantId, Combatant>,
  excludeCoverFrom?: CombatantId,
): ObjectContributions {
  const out = emptyObjectContributions();
  for (const c of liveProfiledObjects(combatants)) {
    const profile = c.base.objectProfile!;
    const isSelf = excludeCoverFrom !== undefined && c.id === excludeCoverFrom;
    for (const cell of space.footprint(c.pos!, c.base.size).cells()) {
      const key = space.key(cell);
      if (profile.blocksSight) out.blocksSightCells.add(key);
      if (!isSelf) {
        if (profile.cover === "hard") out.hardCoverCells.add(key);
        else if (profile.cover === "soft") out.softCoverCells.add(key);
      }
    }
  }
  return out;
}
